import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { collection, limit, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';

type SeoSnapshot = {
  id: string;
  periodLabel?: string;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
  delta?: {
    clicks?: number;
    impressions?: number;
    ctr?: number;
    position?: number;
  };
  createdAt?: Timestamp;
};

type SeoExperiment = {
  id: string;
  dateApplied?: Timestamp | string;
  url?: string;
  query?: string;
  changeType?: string;
  oldValue?: string;
  newValue?: string;
  hypothesis?: string;
  kpiTarget?: string;
  status?: string;
  review14?: string;
  review28?: string;
  review56?: string;
  resultSummary?: string;
  nextAction?: string;
  intentVerdict?: 'Pass' | 'Fix' | string;
  positioningVerdict?: 'Pass' | 'Fix' | string;
  conversionVerdict?: 'Pass' | 'Fix' | string;
  snippetVerdict?: 'Pass' | 'Fix' | string;
  updatedAt?: Timestamp;
};

const statusColor = (status?: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'won') return 'success';
  if (s === 'lost') return 'error';
  if (s === 'reviewing') return 'warning';
  if (s === 'applied') return 'info';
  return 'default';
};

const verdictColor = (verdict?: string) => {
  const v = (verdict || '').toLowerCase();
  if (v === 'pass') return 'success';
  if (v === 'fix') return 'warning';
  return 'default';
};

const toDate = (value?: Timestamp | string) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  try {
    return value.toDate();
  } catch {
    return null;
  }
};

const isReviewDue = (dateApplied?: Timestamp | string, days?: number, reviewValue?: string) => {
  if (!days || !dateApplied) return false;
  if (reviewValue && reviewValue.toLowerCase() !== 'pending') return false;
  const d = toDate(dateApplied);
  if (!d) return false;
  const dueAt = new Date(d);
  dueAt.setDate(dueAt.getDate() + days);
  return new Date() >= dueAt;
};

const SeoDashboard: React.FC = () => {
  const [snapshots, setSnapshots] = useState<SeoSnapshot[]>([]);
  const [experiments, setExperiments] = useState<SeoExperiment[]>([]);

  useEffect(() => {
    const snapQuery = query(collection(db, 'seoSnapshots'), orderBy('createdAt', 'desc'), limit(12));
    const expQuery = query(collection(db, 'seoExperiments'), orderBy('updatedAt', 'desc'), limit(200));

    const unsub1 = onSnapshot(snapQuery, (q) => {
      const rows: SeoSnapshot[] = q.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SeoSnapshot, 'id'>) }));
      setSnapshots(rows);
    });

    const unsub2 = onSnapshot(expQuery, (q) => {
      const rows: SeoExperiment[] = q.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SeoExperiment, 'id'>) }));
      setExperiments(rows);
    });

    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  const latest = snapshots[0];
  const previous = snapshots[1];

  const delta = useMemo(() => {
    if (latest?.delta) {
      return {
        clicks: latest.delta.clicks || 0,
        impressions: latest.delta.impressions || 0,
        ctr: latest.delta.ctr || 0,
        position: latest.delta.position || 0
      };
    }
    if (!latest || !previous) return null;
    return {
      clicks: (latest.clicks || 0) - (previous.clicks || 0),
      impressions: (latest.impressions || 0) - (previous.impressions || 0),
      ctr: (latest.ctr || 0) - (previous.ctr || 0),
      position: (latest.position || 0) - (previous.position || 0)
    };
  }, [latest, previous]);

  const statusCount = useMemo(() => {
    return experiments.reduce(
      (acc, e) => {
        const key = (e.status || 'Planned').toLowerCase();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [experiments]);

  const dueReviews = useMemo(() => {
    return {
      d14: experiments.filter((e) => isReviewDue(e.dateApplied, 14, e.review14)).length,
      d28: experiments.filter((e) => isReviewDue(e.dateApplied, 28, e.review28)).length,
      d56: experiments.filter((e) => isReviewDue(e.dateApplied, 56, e.review56)).length
    };
  }, [experiments]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        SEO Monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Snapshots + experiment tracker + marketing-quality verdicts
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Clicks', value: latest?.clicks ?? '-', delta: delta?.clicks },
          { label: 'Impressions', value: latest?.impressions ?? '-', delta: delta?.impressions },
          { label: 'CTR %', value: latest?.ctr?.toFixed?.(2) ?? '-', delta: delta?.ctr },
          { label: 'Avg Position', value: latest?.position?.toFixed?.(2) ?? '-', delta: delta?.position }
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {item.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: (item.delta || 0) >= 0 ? 'success.main' : 'error.main' }}
                >
                  {item.delta == null ? '—' : `${item.delta > 0 ? '+' : ''}${item.delta.toFixed(2)} vs previous`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Experiment Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {Object.keys(statusCount).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No experiments yet</Typography>
              ) : (
                Object.entries(statusCount).map(([k, v]) => (
                  <Chip key={k} size="small" label={`${k}: ${v}`} />
                ))
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reviews Due
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" color={dueReviews.d14 > 0 ? 'warning' : 'default'} label={`D+14: ${dueReviews.d14}`} />
              <Chip size="small" color={dueReviews.d28 > 0 ? 'warning' : 'default'} label={`D+28: ${dueReviews.d28}`} />
              <Chip size="small" color={dueReviews.d56 > 0 ? 'warning' : 'default'} label={`D+56: ${dueReviews.d56}`} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 110 }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 300 }}>URL / Query</TableCell>
                  <TableCell sx={{ minWidth: 170 }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>KPI Target</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>Next Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experiments.slice(0, 15).map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ verticalAlign: 'top' }}>{toDate(e.dateApplied || e.updatedAt)?.toLocaleDateString() || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>{e.url || '-'}</Typography>
                      {e.query && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Query: {e.query}</Typography>}
                    </TableCell>
                    <TableCell>{e.changeType || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(e.status) as any} label={e.status || 'Planned'} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>{e.kpiTarget || '-'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>{e.nextAction || '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {experiments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No experiment records in Firebase yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={4}>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>Marketing Quality Verdicts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experiments.slice(0, 6).map((e) => (
                  <TableRow key={`v-${e.id}`}>
                    <TableCell sx={{ maxWidth: 140 }}>
                      <Typography variant="caption" noWrap>{e.url || e.query || e.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Chip size="small" color={verdictColor(e.intentVerdict) as any} label={`Intent: ${e.intentVerdict || '-'}`} />
                        <Chip size="small" color={verdictColor(e.positioningVerdict) as any} label={`Pos: ${e.positioningVerdict || '-'}`} />
                        <Chip size="small" color={verdictColor(e.conversionVerdict) as any} label={`Conv: ${e.conversionVerdict || '-'}`} />
                        <Chip size="small" color={verdictColor(e.snippetVerdict) as any} label={`Snippet: ${e.snippetVerdict || '-'}`} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {experiments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No verdict records yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Snapshot</TableCell>
                  <TableCell align="right">Clicks</TableCell>
                  <TableCell align="right">Impr.</TableCell>
                  <TableCell align="right">CTR%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshots.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.periodLabel || s.createdAt?.toDate().toLocaleDateString() || '-'}</TableCell>
                    <TableCell align="right">{s.clicks ?? '-'}</TableCell>
                    <TableCell align="right">{s.impressions ?? '-'}</TableCell>
                    <TableCell align="right">{s.ctr?.toFixed?.(2) ?? '-'}</TableCell>
                  </TableRow>
                ))}
                {snapshots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No snapshot records in Firebase yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SeoDashboard;
